/**
 * PM Platform — Menu aim / hover intent para el mega menú "Categorías"
 * (custom element nativo <vertical-tabs-menu>, assets/scripts.js).
 *
 * Problema: Athens cambia de categoría con mouseenter inmediato en cada
 * .vertical-tabs-menu-tab. En touchpad, una trayectoria diagonal hacia el
 * contenido de la derecha puede rozar otra categoría de paso y reemplazar
 * accidentalmente la que el usuario realmente quería ver.
 *
 * Mecanismo: un listener capture:true en .vertical-tabs-menu-tabs recibe el
 * mouseenter de cada tab ANTES que el listener nativo de Athens (registrado
 * directamente en el <button>, sin capture) -- la fase de captura de un
 * ancestro siempre antecede a la fase AT_TARGET del descendiente, aunque
 * mouseenter no haga bubble; esto se confirmó empíricamente en Chrome real
 * antes de escribir este archivo. Cuando la trayectoria reciente del puntero
 * indica intención real hacia el contenido, se llama event.stopPropagation()
 * -- Athens nunca recibe ese mouseenter -- y la categoría candidata queda en
 * espera un breve período; si no hay intención hacia el contenido (recorrido
 * vertical normal), no se hace nada y Athens procesa el mouseenter como
 * siempre. switchTab() sigue siendo el único método que cambia de categoría;
 * este archivo nunca reimplementa esa lógica, solo decide CUÁNDO se le
 * permite a Athens ejecutarla, o la ejecuta él mismo tras el período de
 * espera usando el mismo método público.
 *
 * No toca assets/scripts.js. No intercepta focus/click sobre las TABS
 * internas -- foco de teclado entre categorías sigue siendo inmediato.
 *
 * CAPA DE APERTURA/CIERRE DEL MENU COMPLETO (esta ronda, auditoria real
 * pre-presentacion): confirmado por lectura directa de assets/scripts.js
 * que <vertical-tabs-menu> (VerticalTabsMenu, connectedCallback) SOLO
 * implementa el cambio de pestaña interna (switchTab) -- no existe alli
 * ningun listener de click sobre .vertical-tabs-menu-trigger, ningun
 * mecanismo de abrir/cerrar el panel completo, y la clase ".is-open" que
 * assets/base.css SI reconoce en su regla de visibilidad
 * (".vertical-tabs-menu:hover, .vertical-tabs-menu.is-open") nunca es
 * agregada por ningun script existente -- confirmado, es codigo muerto
 * hasta esta ronda. La visibilidad real dependia EXCLUSIVAMENTE de
 * :hover, que bajo emulacion/dispositivos tactiles reales (hover:none)
 * puede quedar "pegado" tras un tap (no hay mouse real que "se aleje"),
 * sin ninguna ruta de cierre explicita disponible -- diagnostico real en
 * Chrome Responsive (1155px, pointer:coarse) confirmo exactamente ese
 * sintoma. Esta capa agrega click/tap -> ".is-open" (leido por la regla
 * nativa de base.css, sin tocar ese archivo), outside-click, Escape y
 * sincronizacion de aria-expanded, preservando intacto el mecanismo de
 * :hover para mouse real (ver assets/pm-platform-header.css para el
 * ajuste puntual que neutraliza el :hover "pegado" bajo hover:none).
 */
(function () {
  "use strict";

  var GRACE_PERIOD_MS = 500;
  var POINTER_SAMPLE_MAX_AGE_MS = 200;
  var TRAJECTORY_TOLERANCE_PX = 30;
  // Por debajo de este ratio (|dx| / |dy|), el movimiento se considera
  // predominantemente vertical (ej. dx:2 dy:100) y nunca se protege --
  // evita que un leve componente horizontal accidental bloquee la
  // navegación vertical deliberada.
  var VERTICAL_RATIO_THRESHOLD = 0.15;

  var initializedMenus = new WeakSet();

  function isDesktop() {
    return window.matchMedia("(min-width: 990px)").matches;
  }

  // true solo con mouse real (no touch/coarse, real o hibrido). Se usa
  // para decidir si un segundo click debe forzar el cierre visual pese a
  // :hover -- en hover:none esa clase nunca hace falta (el CSS de
  // hover:none ya neutraliza :hover cuando falta .is-open) y agregarla
  // ahi arriesgaria quedar huerfana si mouseleave nunca llega a disparar.
  function canHover() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  function initMenu(menu) {
    if (initializedMenus.has(menu)) return;

    var tabsContainer = menu.querySelector(".vertical-tabs-menu-tabs");
    var content = menu.querySelector(".vertical-tabs-menu-content");
    var trigger = menu.querySelector(".vertical-tabs-menu-trigger");

    // Mejora progresiva: si falta cualquier pieza esperada (incluido
    // trigger, requerido por la capa de apertura/cierre mas abajo), o
    // switchTab no es un metodo publico real, no se inicializa nada y
    // Athens sigue funcionando exactamente igual que hoy. Las TRES
    // dependencias estructurales se validan ANTES de marcar
    // initializedMenus -- si se marcara antes y alguna faltara, la
    // instancia quedaria permanentemente a medio inicializar (un futuro
    // shopify:section:load con el DOM ya completo jamas podria
    // reintentar, por el guard "if (initializedMenus.has(menu)) return"
    // del inicio de esta funcion).
    if (!tabsContainer || !content || !trigger || typeof menu.switchTab !== "function") {
      return;
    }

    initializedMenus.add(menu);

    var previousPointer = null;
    var lastPointer = null;
    var pendingTab = null;
    var pendingTimer = null;

    function cancelPending() {
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = null;
      pendingTab = null;
    }

    function getActiveIndex() {
      var activeTab = menu.querySelector(".vertical-tabs-menu-tab.is-active");
      return activeTab ? Number(activeTab.dataset.tabIndex) : null;
    }

    // mousemove solo guarda coordenadas -- ningún cálculo geométrico aquí.
    tabsContainer.addEventListener("mousemove", function (event) {
      previousPointer = lastPointer;
      lastPointer = { x: event.clientX, y: event.clientY, time: performance.now() };
    });

    // Equivalente geométrico del "safe triangle" clásico de menu-aim: se
    // proyecta la trayectoria reciente (previousPointer -> lastPointer)
    // hasta el borde izquierdo real de .vertical-tabs-menu-content (medido
    // en el momento, nunca cacheado) y se comprueba si esa proyección cae
    // dentro de su rango vertical, con tolerancia para trayectorias
    // imperfectas de trackpad.
    function isMovingTowardContent() {
      if (!previousPointer || !lastPointer) return false;

      var now = performance.now();
      if (
        now - lastPointer.time > POINTER_SAMPLE_MAX_AGE_MS ||
        lastPointer.time - previousPointer.time > POINTER_SAMPLE_MAX_AGE_MS
      ) {
        return false; // muestra obsoleta: no inferir intención sobre datos viejos
      }

      var dx = lastPointer.x - previousPointer.x;
      var dy = lastPointer.y - previousPointer.y;

      if (dx <= 0) return false; // el contenido está a la derecha del sidebar

      var horizontalRatio = Math.abs(dx) / Math.max(Math.abs(dy), 1);
      if (horizontalRatio < VERTICAL_RATIO_THRESHOLD) return false;

      var contentRect = content.getBoundingClientRect();
      var projectedY = previousPointer.y + (dy / dx) * (contentRect.left - previousPointer.x);

      return (
        projectedY >= contentRect.top - TRAJECTORY_TOLERANCE_PX &&
        projectedY <= contentRect.bottom + TRAJECTORY_TOLERANCE_PX
      );
    }

    function scheduleActivation(tab, candidateIndex) {
      cancelPending();
      pendingTab = tab;
      pendingTimer = setTimeout(function () {
        var stillPending = pendingTab === tab;
        pendingTab = null;
        pendingTimer = null;

        if (!stillPending) return;
        if (!menu.matches(":hover")) return;
        if (!lastPointer) return;

        // El usuario debe seguir físicamente sobre el candidato (o dentro
        // de él) para que el cambio diferido tenga sentido -- evita activar
        // una categoría sobre un contexto que ya cambió.
        var elementUnderPointer = document.elementFromPoint(lastPointer.x, lastPointer.y);
        if (elementUnderPointer !== tab && !(elementUnderPointer && tab.contains(elementUnderPointer))) {
          return;
        }

        menu.switchTab(candidateIndex);
      }, GRACE_PERIOD_MS);
    }

    tabsContainer.addEventListener(
      "mouseenter",
      function (event) {
        var tab = event.target.closest && event.target.closest(".vertical-tabs-menu-tab");
        if (!tab || !tabsContainer.contains(tab)) return;

        var candidateIndex = Number(tab.dataset.tabIndex);
        if (!Number.isFinite(candidateIndex)) return;

        if (candidateIndex === getActiveIndex()) {
          cancelPending();
          return;
        }

        if (isMovingTowardContent()) {
          event.stopPropagation(); // Athens no recibe este mouseenter
          scheduleActivation(tab, candidateIndex);
        } else {
          cancelPending();
          // Sin stopPropagation: Athens procesa el mouseenter normalmente,
          // switchTab() inmediato -- recorrido vertical intacto.
        }
      },
      true
    );

    // Intención confirmada: el candidato pendiente queda descartado por
    // completo, sin excepción.
    content.addEventListener("mouseenter", function () {
      cancelPending();
    });

    // Athens ya resetea a la categoría 1 en este mismo evento
    // (assets/scripts.js); solo cancelamos nuestro propio timer para que
    // no dispare un cambio tardío sobre un menú ya cerrado.
    menu.addEventListener("mouseleave", function () {
      cancelPending();
    });

    // --- Capa de apertura/cierre (ver comentario de archivo, arriba) ---
    // trigger ya fue localizado y validado junto con tabsContainer/content
    // antes de initializedMenus.add(menu); se reutiliza esa misma variable.
    openMenus.push({ menu: menu, trigger: trigger });

    // .is-click-closed: una accion explicita (segundo click / Escape)
    // debe poder cerrar visualmente el menu aunque :hover siga
    // verdadero (mouse fisicamente encima) -- CSS pura (:hover) no tiene
    // forma de saber que el usuario ya pidio cerrar. assets/pm-platform-
    // header.css le da a esta clase prioridad real sobre :hover, sin
    // !important (especificidad de #header). Se agrega SOLO con mouse
    // real (canHover(), ver arriba): en touch no hace falta (la regla
    // hover:none ya existente resuelve el cierre solo con .is-open) y
    // evita el riesgo de quedar huerfana si mouseleave no llega a
    // disparar. Se limpia siempre en mouseleave (abajo): tras salir
    // fisicamente, un mouseenter nuevo debe volver a abrir por hover
    // normal, sin arrastrar el cierre forzado de la interaccion previa.
    trigger.addEventListener("click", function () {
      if (menu.classList.contains("is-open")) {
        menu.classList.remove("is-open");
        if (canHover()) {
          menu.classList.add("is-click-closed");
        }
        trigger.setAttribute("aria-expanded", "false");
      } else {
        menu.classList.remove("is-click-closed");
        menu.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }
    });

    // aria-expanded debe reflejar el estado VISUAL real, no solo el pin
    // por click: mientras el mouse real esta sobre el menu (:hover
    // nativo), el panel es efectivamente visible aunque no este
    // pinneado. El guard evita pisar el valor "true" ya fijado por el
    // pin al salir con el mouse mientras sigue pinneado.
    menu.addEventListener("mouseenter", function () {
      if (!menu.classList.contains("is-open")) {
        trigger.setAttribute("aria-expanded", "true");
      }
    });

    menu.addEventListener("mouseleave", function () {
      menu.classList.remove("is-click-closed");
      if (!menu.classList.contains("is-open")) {
        trigger.setAttribute("aria-expanded", "false");
      }
    });

    menu.addEventListener("keydown", function (event) {
      if (event.key !== "Escape" && event.key !== "Esc") return;
      menu.classList.remove("is-open");
      // Igual que el click, pero sin gate de canHover(): Escape debe
      // anular el :hover activo AHORA MISMO sea real o "pegado" por
      // touch -- si el cursor no esta realmente encima, matches(':hover')
      // ya es false y esta clase ni se agrega.
      if (menu.matches(":hover")) {
        menu.classList.add("is-click-closed");
      }
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus();
    });
  }

  // Un unico listener global (no uno por instancia) cierra cualquier
  // <vertical-tabs-menu class="is-open"> cuando el click cae fuera de
  // el -- mismo patron ya usado y probado en
  // assets/pm-header-contact-dropdown.js. openMenus se llena una vez
  // por menu real inicializado (initMenu ya es idempotente via
  // initializedMenus), nunca se registra un listener nuevo por click.
  var openMenus = [];

  document.addEventListener("click", function (event) {
    openMenus.forEach(function (entry) {
      if (!entry.menu.classList.contains("is-open")) return;
      if (entry.menu.contains(event.target)) return;
      entry.menu.classList.remove("is-open", "is-click-closed");
      entry.trigger.setAttribute("aria-expanded", "false");
    });
  });

  function initAll(root) {
    if (!isDesktop()) return;
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll("vertical-tabs-menu").forEach(initMenu);
  }

  initAll();

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
